import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, User, Tag, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import AdBanner from '../components/AdBanner';
import Breadcrumb from '../components/Breadcrumb';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPage = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = selectedCategory ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API}/blog/posts${params}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const formatDate = (dateString) => {
    const locale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const blogTitle = t('blog.blog');
  const latestPostsText = t('blog.latestPosts');

  return (
    <>
      <Helmet>
        <title>{`${blogTitle} - `}</title>
        <meta name="description" content={latestPostsText} />
      </Helmet>

      <div className="min-h-screen bg-zinc-800">
        {/* Top Banner Ad */}
        <AdBanner position="top" />
        
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: blogTitle, href: null }]} />
        
        {/* Hero Section */}
        <div className="border-b border-zinc-800 py-12 bg-gradient-to-b from-zinc-900 to-black">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{blogTitle}</h1>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              {latestPostsText}
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                </div>
              ) : posts.length > 0 ? (
                <div className="space-y-8">
                  {posts.map((post) => (
                    <article 
                      key={post.id}
                      className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden hover:border-zinc-700 transition-colors"
                    >
                      {post.featured_image && (
                        <Link to={`/blog/${post.slug}`}>
                          <img 
                            src={post.featured_image} 
                            alt={post.title}
                            className="w-full h-64 object-cover hover:opacity-90 transition-opacity"
                          />
                        </Link>
                      )}
                      <div className="p-6">
                        <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-500 mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(post.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {post.author_name}
                          </span>
                          {post.category && (
                            <Badge 
                              className="bg-emerald-500/20 text-emerald-400 cursor-pointer hover:bg-emerald-500/30"
                              onClick={() => setSelectedCategory(post.category)}
                            >
                              {post.category}
                            </Badge>
                          )}
                        </div>
                        
                        <Link to={`/blog/${post.slug}`}>
                          <h2 className="text-2xl font-bold text-white mb-3 hover:text-emerald-400 transition-colors">
                            {post.title}
                          </h2>
                        </Link>
                        
                        <p className="text-zinc-400 mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>

                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {post.tags.map((tag, index) => (
                              <span 
                                key={index}
                                className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <Link to={`/blog/${post.slug}`}>
                          <Button variant="ghost" className="text-emerald-400 hover:text-emerald-300 p-0">
                            {t('blog.readMore')} <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-zinc-400 text-lg mb-4">{t('blog.noPosts')}</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:w-80">
              <div className="sticky top-24 space-y-6">
                {/* Categories */}
                {categories.length > 0 && (
                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">{t('blog.category')}</h3>
                    <div className="space-y-2">
                      <button
                        onClick={() => setSelectedCategory(null)}
                        className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          !selectedCategory 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'text-zinc-400 hover:bg-zinc-800'
                        }`}
                      >
                        {t('common.all')}
                      </button>
                      {categories.map((cat) => (
                        <button
                          key={cat.name}
                          onClick={() => setSelectedCategory(cat.name)}
                          className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                            selectedCategory === cat.name 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : 'text-zinc-400 hover:bg-zinc-800'
                          }`}
                        >
                          {cat.name} ({cat.count})
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPage;
